import { Box, Button, Divider, Flex, Stack } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { Practice, Provider } from '../../../types';
import { CustomField } from '../../../types/custom-fields/types';
import MapWithMarker from '../../../components/map-with-marker/MapWithMarker';
import { analytics } from '../../../utils/analytics';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import AbsencesTable from '../providers/AbsencesTable';
import MarketPlansList from '../state/MarketPlansList';
import ProvidersUnderPractice from './ProviderUnderPractice';

interface PracticeResourceSectionProps {
  practiceId: string;
  plansCount?: number;
  practiceCount?: number;
  providerCount?: number | string;
  name?: string;
  providers?: Provider[];
  customFields?: CustomField[]; // Add customFields prop
  currentPractice?: Practice; // Add current practice data
  isCustomFieldsLoading?: boolean; // Add loading state for custom fields
  customFieldsError?: string | null; // Add error state for custom fields
  specialtyOptions?: { label: string; value: string }[]; // Add specialtyOptions
  isSpecialtyOptionsLoading?: boolean; // Add specialtyOptions loading state
  duplicateProviders?: Record<string, string>;
}

const PracticeResourceSection: React.FC<
  PracticeResourceSectionProps
> = ({
  practiceId,
  plansCount,
  providerCount,
  name,
  providers,
  customFields,
  currentPractice,
  isCustomFieldsLoading = false,
  customFieldsError = null,
  specialtyOptions = [],
  isSpecialtyOptionsLoading = false,
  duplicateProviders,
}) => {
  const [selectedView, setSelectedView] = useState<
    'practices' | 'providers' | 'plans' | 'absences' | 'map'
  >('providers');
  //const [viewMode, setViewMode] = useState<'list' | 'table'>('list');

  // Memoize map location to prevent unnecessary re-renders
  const mapLocation = useMemo(() => {
    if (!currentPractice) return null;

    const address = constructPracticeAddress(currentPractice);
    if (!address) return null;

    return {
      address: address,
      name: currentPractice.name,
      id: currentPractice.id,
    };
  }, [currentPractice]);

  const renderContent = () => {
    if (practiceId && name) {
      switch (selectedView) {
        case 'providers':
          return (
            <ProvidersUnderPractice
              providers={providers}
              name={name}
              practiceId={practiceId}
              customFields={customFields}
              currentPractice={currentPractice}
              isCustomFieldsLoading={isCustomFieldsLoading}
              customFieldsError={customFieldsError}
              specialtyOptions={specialtyOptions}
              isSpecialtyOptionsLoading={isSpecialtyOptionsLoading}
              duplicateProviders={duplicateProviders}
            />
          );
        case 'plans':
          return (
            <MarketPlansList practiceIds={[practiceId]} name={name} />
          );
        case 'absences':
          return (
            <AbsencesTable
              practiceId={practiceId}
              providerIds={(providers ?? []).map(
                (provider) => provider.id
              )}
              providers={(providers ?? []).map(
                ({
                  id,
                  firstname,
                  middlename,
                  lastname,
                  suffix,
                }) => ({
                  id,
                  name: [lastname, middlename, firstname, suffix]
                    .filter(Boolean) // Remove undefined or null values
                    .join(' '), // Join with a space
                })
              )} // Map and pass id and full name
            />
          );
        case 'map':
          return (
            <Box width="100%" height="600px" borderRadius="0.5rem" overflow="hidden">
              {mapLocation ? (
                <MapWithMarker locations={[mapLocation]} />
              ) : (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  height="100%"
                  textAlign="center"
                  bg="gray.50"
                  borderRadius="0.5rem"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Box fontSize="1.125rem" fontWeight="600" color="text.secondary" mb="0.5rem">
                    No Map Available
                  </Box>
                  <Box fontSize="0.875rem" color="gray.600">
                    Practice address information is not available.
                  </Box>
                </Flex>
              )}
            </Box>
          );
        default:
          return null;
      }
    }
  };

  return (
    <Box ml="2rem" mr="2rem" mb="5rem">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb="1rem"
      >
        <Flex gap="1rem">
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'providers'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('providers')}
            color={
              selectedView === 'practices'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'practices' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Providers {providerCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'plans' ? 'background.blue100' : 'none'
            }
            onClick={() => setSelectedView('plans')}
            color={
              selectedView === 'plans'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'plans' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Plans {plansCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'absences'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('absences')}
            color={
              selectedView === 'absences'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'absences' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Absences
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'map'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => {
              setSelectedView('map');
              analytics.trackEvent('view', 'map');
            }}
            color={
              selectedView === 'map'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'map' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Map
          </Button>
        </Flex>
        {/* <Flex gap="1rem" alignItems="center">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        </Flex> */}
      </Flex>
      <Divider mb="1rem" />
      <Box>
        <Stack gap="1rem">{renderContent()}</Stack>
      </Box>
    </Box>
  );
};

export default PracticeResourceSection;